<template>
  <div>
    <!-- Hero -->
    <div class="hero w-full pb-8">
      <!-- Title area -->
      <div class="text-center px-3 pt-16 pb-8">
        <HeroTitle />
      </div>

      <!-- Cards -->
      <div
        v-if="
          $branding.hasFeature(FEATURES.JOB_MARKET) &&
          $branding.hasFeature(FEATURES.PROFESSIONALS)
        "
        class="hero-cards max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <SearchBox />
        <QuickOffer
          @requestTender="
            onRequestTender({ source: 'quickOffer', description: $event })
          "
        />
      </div>
    </div>

    <!-- Dynamic modules from conductor config -->
    <component
      :is="mod.component"
      v-for="mod in resolvedModules"
      :key="`${mod.slug}-${mod.position}`"
      :module-config="mod"
    />
  </div>
</template>

<script>
import { computed } from "@nuxtjs/composition-api";
import QuickOffer from "~/components/_refactored/app/boxes/QuickOffer.vue";
import SearchBox from "~/components/people/SearchBox.vue";
import HeroTitle from "~/components/_refactored/indexPage/HeroTitle.vue";
import useFrontPageModules from "~/composables/useFrontPageModules";
import { useRequestTender } from "~/composables/requestTender";
import { FEATURES } from "~/config/pages/features.config";
import { getBaseSlug } from "~/config/frontPageModules";

export default {
  name: "ModularIndexPage",
  components: {
    HeroTitle,
    QuickOffer,
    SearchBox,
  },
  setup() {
    const { visibleModules, getComponent } = useFrontPageModules();

    // Pre-resolve async components so Vue doesn't recreate them each render
    const componentCache = {};
    const resolvedModules = computed(() =>
      visibleModules.value.map((mod) => {
        const base = getBaseSlug(mod.slug);
        if (!componentCache[base]) {
          componentCache[base] = getComponent(mod.slug);
        }
        return {
          ...mod,
          component: componentCache[base],
        };
      })
    );

    return {
      ...useRequestTender(),
      resolvedModules,
      FEATURES,
    };
  },
  head() {
    const description = `${this.$t("landing.header.subtitle1")} ${this.$t(
      "landing.header.subtitle2"
    )}`;
    return {
      title: this.$t("landing.meta.metaTitle"),
      meta: [
        {
          hid: "og:title",
          property: "og:title",
          content: this.$t("landing.meta.metaTitle"),
        },
        {
          hid: "description",
          name: "description",
          content: description,
        },
        {
          hid: "og:description",
          property: "og:description",
          content: description,
        },
        {
          hid: "og:url",
          property: "og:url",
          content: `${this.$helpers.topDomain}`,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: this.$helpers.topDomain,
        },
      ],
    };
  },
  mounted() {
    this.$trackers.pageview({
      pagename: this.$trackers.pageNames.HOME_PAGE,
    });
  },
  beforeDestroy() {
    this.$trackers.pageleave({
      pagename: this.$trackers.pageNames.HOME_PAGE,
    });
  },
  methods: {
    onRequestTender({ person, source, description } = {}) {
      this.requestTender({
        source,
        person,
        profession: this.profession,
        city: this.city,
        description,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use "styles/_all-members" as global;

.hero {
  background: linear-gradient(
      rgba(0, 0, 0, 0.45),
      rgba(0, 0, 0, 0.55)
    ),
    url("~@/assets/img/lading-hero-c.jpg") center center;
  background-size: cover;
}

// Compact card overrides for hero section
.hero-cards {
  ::v-deep .QuickOffer {
    gap: 0.25rem;
    padding: 1rem 1.25rem;

    .max-h-20 {
      margin-bottom: 0.25rem;
    }

    [class*="my-4"] {
      margin-top: 0.25rem !important;
      margin-bottom: 0.25rem !important;
    }

    textarea {
      height: 3.5rem;
      min-height: 3.5rem;
    }
  }

  ::v-deep .JoszakiCard {
    height: 100%;
    gap: 1rem;
    padding: 1.25rem;

    > button,
    > a {
      margin-top: auto;
    }
  }
}
</style>
